import React from 'react';
import { useTranslation } from 'react-i18next';
import MainLayout from '@/components/layout/MainLayout';
import { SEOHead } from '@/components/seo/SEOHead';
import { getOgImage } from '@/config/ogImages';
import LocalizedLink from '@/i18n/LocalizedLink';
import UpcomingEventsTeaser from '@/components/events/UpcomingEventsTeaser';
import TrendingEventsSection from '@/components/trending/TrendingEventsSection';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Building, Users, Calendar, Phone, CheckCircle, ArrowRight, Dna, TrendingUp, Target } from 'lucide-react';

export default function HomePage() {
  const { t } = useTranslation();
  // FAQ Schema for Perplexity and other AI search engines
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is Jigsaw Events Hub?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Jigsaw Events Hub is an independent directory of business events and a delegate-support platform for group accommodation and meeting rooms. Unless explicitly stated, we are not affiliated with the events, venues or organisers listed. Our research tools help visitors discover events, understand key details and plan attendance. Please verify the organiser, dates, venue and destination website before booking, paying or travelling."
        }
      },
      {
        "@type": "Question",
        "name": "What events does Jigsaw Events Hub cover?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We cover major B2B events including CES (Consumer Electronics Show), Mobile World Congress (MWC), BETT Show, Hannover Messe, Farnborough Airshow, VivaTech, and many more. Events span sectors like Technology, Healthcare, Manufacturing, Finance, and Retail."
        }
      },
      {
        "@type": "Question",
        "name": "Which cities have events listed on Jigsaw Events Hub?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We list events in 40+ cities worldwide including: London (ExCeL, Olympia), Birmingham (NEC), New York, Las Vegas, Dubai, Paris, Barcelona, Berlin, Singapore, Hong Kong, Amsterdam, and many more major exhibition centers."
        }
      },
      {
        "@type": "Question",
        "name": "How can I find upcoming business events?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Visit events.jigsawconferences.co.uk and browse by city (e.g., /events/london), by venue (e.g., /events/london/excel-london), or by sector. You can also subscribe to our calendar feeds for automatic updates."
        }
      },
      {
        "@type": "Question",
        "name": "What is the Event Genome feature?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Event Genome is our AI-assisted research and insight tool. It helps assess likely audience fit, networking potential, sector relevance and expected value to support event research. It is not a statement of organiser partnership or endorsement, and visitors should verify event details with the organiser before booking. Jigsaw Events Hub is an independent platform and may not be partnered with or affiliated with the listed events unless explicitly stated."
        }
      }
    ]
  };

  // E-E-A-T Author Schema - Establishes expertise and trustworthiness
  const authorSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Jigsaw Events Team",
    "jobTitle": "Event Industry Specialists",
    "worksFor": {
      "@type": "Organization",
      "name": "Jigsaw Conferences",
      "url": "https://jigsawconferences.co.uk"
    },
    "description": "Our team of event industry specialists has over 20 years of experience helping businesses find and attend the right trade shows, exhibitions, and conferences worldwide.",
    "knowsAbout": [
      "Business Events",
      "Trade Shows",
      "Exhibition Planning",
      "Conference Venues",
      "Event ROI Analysis",
      "B2B Networking",
      "Venue Selection"
    ],
    "alumniOf": {
      "@type": "Organization",
      "name": "Event Industry Council"
    }
  };

  // Organization Schema with enhanced E-E-A-T signals
  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Jigsaw Conferences",
    "legalName": "Jigsaw Conferences Ltd",
    "url": "https://events.jigsawconferences.co.uk",
    "logo": "https://events.jigsawconferences.co.uk/logo.png",
    "description": "Leading business events directory with AI-powered event intelligence. Covering 124+ events across 40+ cities worldwide since 2003. Trusted by 640,000+ event organisers.",
    "foundingDate": "2003",
    "foundingLocation": "United Kingdom",
    "numberOfEmployees": {
      "@type": "QuantitativeValue",
      "minValue": 10,
      "maxValue": 50
    },
    "award": "20+ Years in Event Industry",
    "slogan": "Smarter Event Decisions, Powered by AI",
    "knowsAbout": [
      "Business Events Directory",
      "Trade Show Planning",
      "Exhibition Venues",
      "Conference Accommodation",
      "Event Intelligence",
      "AI-Powered Event Analysis"
    ],
    "sameAs": [
      "https://www.linkedin.com/company/jigsaw-conferences",
      "https://twitter.com/jigsawconf"
    ],
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "contactType": "customer service",
        "telephone": "+44-800-121-4470",
        "email": "info@jigsawconferences.co.uk",
        "availableLanguage": "English",
        "areaServed": "Worldwide"
      },
      {
        "@type": "ContactPoint",
        "contactType": "sales",
        "telephone": "+44-800-121-4470",
        "email": "enquiries@jigsawconferences.co.uk"
      }
    ],
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "GB",
      "addressLocality": "United Kingdom"
    }
  };

  // Speakable Schema for Voice Search (Google Assistant, Alexa, Siri)
  const speakableSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Jigsaw Events Hub - Business Events Directory",
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": [
        ".hero-headline",
        ".hero-subheadline", 
        ".faq-answer",
        "h1",
        "h2"
      ],
      "xpath": [
        "/html/body//h1",
        "/html/body//article//p[1]"
      ]
    },
    "url": "https://events.jigsawconferences.co.uk"
  };

  // WebSite Schema for sitelinks searchbox
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Jigsaw Events Hub",
    "url": "https://events.jigsawconferences.co.uk",
    "description": "Discover 124+ business events, trade shows and exhibitions across 40+ cities. AI-powered insights for smarter event decisions.",
    "publisher": {
      "@type": "Organization",
      "name": "Jigsaw Conferences",
      "url": "https://jigsawconferences.co.uk"
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://events.jigsawconferences.co.uk/events?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  const faqs = [1, 2, 3, 4, 5];

  return (
    <MainLayout>
      <SEOHead
        title="Jigsaw Events Hub | 124+ Business Events & Trade Shows Worldwide | AI-Powered Intelligence"
        description="Discover 124+ business events, trade shows and exhibitions across 40+ cities including London, New York, Dubai. AI-powered Event Genome predicts your ROI. Free group accommodation. Trusted since 2003."
        url="https://events.jigsawconferences.co.uk"
        image={getOgImage('home')}
      />
      
      {/* Structured Data for AI/LLM visibility */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(authorSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(speakableSchema) }} />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-950 via-blue-900 to-orange-800 text-white py-20 md:py-28 relative overflow-hidden">
        {/* Gradient Orbs - Glowing depth effect */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-br from-orange-500/20 to-transparent rounded-full blur-3xl transform translate-x-1/3 -translate-y-1/4" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-blue-500/15 to-transparent rounded-full blur-3xl transform -translate-x-1/4 translate-y-1/4" />
        <div className="absolute top-1/2 left-1/2 w-[400px] h-[400px] bg-gradient-to-r from-purple-500/10 to-cyan-500/10 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2" />

        {/* Animated DNA Helix Pattern - SVG */}
        <svg className="absolute inset-0 w-full h-full opacity-[0.12]" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="dna-helix" x="0" y="0" width="100" height="200" patternUnits="userSpaceOnUse">
              <path d="M25 0 Q50 50 75 100 Q50 150 25 200" fill="none" stroke="white" strokeWidth="1" />
              <path d="M75 0 Q50 50 25 100 Q50 150 75 200" fill="none" stroke="white" strokeWidth="1" />
              <line x1="25" y1="25" x2="75" y2="25" stroke="white" strokeWidth="0.5" opacity="0.5" />
              <line x1="30" y1="50" x2="70" y2="50" stroke="white" strokeWidth="0.5" opacity="0.5" />
              <line x1="25" y1="75" x2="75" y2="75" stroke="white" strokeWidth="0.5" opacity="0.5" />
              <line x1="30" y1="100" x2="70" y2="100" stroke="white" strokeWidth="0.5" opacity="0.5" />
              <line x1="25" y1="125" x2="75" y2="125" stroke="white" strokeWidth="0.5" opacity="0.5" />
              <line x1="30" y1="150" x2="70" y2="150" stroke="white" strokeWidth="0.5" opacity="0.5" />
              <line x1="25" y1="175" x2="75" y2="175" stroke="white" strokeWidth="0.5" opacity="0.5" />
            </pattern>
            <linearGradient id="helix-fade" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="white" stopOpacity="0.3" />
              <stop offset="50%" stopColor="white" stopOpacity="0.1" />
              <stop offset="100%" stopColor="white" stopOpacity="0" />
            </linearGradient>
          </defs>
          <rect width="100%" height="100%" fill="url(#dna-helix)" mask="url(#helix-fade)" />
        </svg>

        {/* Neural Network Dots */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-[10%] left-[15%] w-2 h-2 bg-white rounded-full animate-pulse" />
          <div className="absolute top-[25%] left-[25%] w-1.5 h-1.5 bg-orange-400 rounded-full animate-pulse" style={{animationDelay: '0.5s'}} />
          <div className="absolute top-[15%] left-[40%] w-1 h-1 bg-white rounded-full animate-pulse" style={{animationDelay: '1s'}} />
          <div className="absolute top-[35%] left-[55%] w-2 h-2 bg-cyan-400 rounded-full animate-pulse" style={{animationDelay: '0.3s'}} />
          <div className="absolute top-[20%] right-[20%] w-1.5 h-1.5 bg-white rounded-full animate-pulse" style={{animationDelay: '0.7s'}} />
          <div className="absolute top-[45%] right-[30%] w-1 h-1 bg-purple-400 rounded-full animate-pulse" style={{animationDelay: '1.2s'}} />
          <div className="absolute top-[60%] left-[20%] w-1.5 h-1.5 bg-white rounded-full animate-pulse" style={{animationDelay: '0.2s'}} />
          <div className="absolute top-[70%] left-[35%] w-2 h-2 bg-orange-400 rounded-full animate-pulse" style={{animationDelay: '0.8s'}} />
          <div className="absolute top-[55%] right-[15%] w-1 h-1 bg-white rounded-full animate-pulse" style={{animationDelay: '1.5s'}} />
          <div className="absolute top-[80%] right-[25%] w-1.5 h-1.5 bg-cyan-400 rounded-full animate-pulse" style={{animationDelay: '0.4s'}} />
          <div className="absolute top-[40%] left-[10%] w-1 h-1 bg-white rounded-full animate-pulse" style={{animationDelay: '1.1s'}} />
          <div className="absolute bottom-[15%] left-[45%] w-2 h-2 bg-white rounded-full animate-pulse" style={{animationDelay: '0.6s'}} />
        </div>

        {/* Geometric accent lines */}
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-[0.03]">
          <svg className="w-full h-full" viewBox="0 0 400 600" preserveAspectRatio="xMidYMid slice">
            <path d="M0 0 L400 200 L200 600 Z" fill="none" stroke="white" strokeWidth="1" />
            <path d="M100 0 L400 300 L300 600 Z" fill="none" stroke="white" strokeWidth="0.5" />
            <circle cx="300" cy="150" r="80" fill="none" stroke="white" strokeWidth="0.5" />
            <circle cx="200" cy="400" r="120" fill="none" stroke="white" strokeWidth="0.5" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl">
            {/* New badge */}
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-sm font-medium mb-6">
              <Dna className="w-4 h-4 text-orange-400" />
              <span>{t('home.heroBadge')}</span>
            </div>
            
            <h1 className="hero-headline text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              {t('home.heroTitle')}
            </h1>
            <p className="hero-subheadline text-xl md:text-2xl text-blue-100 mb-8">
              {t('home.heroSubtitle')}
            </p>
            
            {/* Feature pills */}
            <div className="flex flex-wrap gap-3 mb-8">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 rounded-full text-sm">
                <Target className="w-4 h-4 text-orange-400" />
                {t('home.pillRoi')}
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 rounded-full text-sm">
                <TrendingUp className="w-4 h-4 text-green-400" />
                {t('home.pillBuzz')}
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 rounded-full text-sm">
                <Users className="w-4 h-4 text-purple-400" />
                {t('home.pillNetworking')}
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 rounded-full text-sm">
                <Building className="w-4 h-4 text-blue-400" />
                {t('home.pillAccommodation')}
              </span>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <LocalizedLink to="/events">
                <Button className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-6 text-lg rounded-lg font-medium w-full sm:w-auto">
                  {t('home.exploreHub')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </LocalizedLink>
              <a href="tel:08001214470">
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg rounded-lg font-medium w-full sm:w-auto">
                  <Phone className="w-5 h-5 mr-2" />
                  0800 121 4470
                </Button>
              </a>
            </div>
            <p className="mt-5 max-w-3xl text-xs leading-6 text-blue-100/90" data-testid="home-affiliation-note">
              {t('home.affiliationNote')}
            </p>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-8 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center items-center gap-6 md:gap-8 text-gray-600 text-sm">
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span>{t('home.trustIso')}</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span>{t('home.trustAnalyzed')}</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span>{t('home.trustAi')}</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span>{t('home.trustSince')}</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span>{t('home.trustFree')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('home.servicesTitle')}</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('home.servicesSubtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="bg-white border border-gray-200 hover:shadow-lg transition-shadow">
              <CardHeader>
                <Building className="w-12 h-12 text-orange-600 mb-4" />
                <CardTitle className="text-xl">{t('home.venueTitle')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">{t('home.venueDesc')}</p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>{t('home.venueLi1')}</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>{t('home.venueLi2')}</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>{t('home.venueLi3')}</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-white border border-gray-200 hover:shadow-lg transition-shadow">
              <CardHeader>
                <Users className="w-12 h-12 text-orange-600 mb-4" />
                <CardTitle className="text-xl">{t('home.accomTitle')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">{t('home.accomDesc')}</p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>{t('home.accomLi1')}</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>{t('home.accomLi2')}</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>{t('home.accomLi3')}</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-white border border-gray-200 hover:shadow-lg transition-shadow">
              <CardHeader>
                <Calendar className="w-12 h-12 text-orange-600 mb-4" />
                <CardTitle className="text-xl">{t('home.hubTitle')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">{t('home.hubDesc')}</p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>{t('home.hubLi1')}</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>{t('home.hubLi2')}</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>{t('home.hubLi3')}</span>
                  </li>
                </ul>
                <LocalizedLink to="/events" className="inline-flex items-center text-orange-600 hover:text-orange-700 font-medium mt-4">
                  {t('home.exploreHub')} <ArrowRight className="w-4 h-4 ml-1" />
                </LocalizedLink>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Upcoming Events Teaser */}
      <UpcomingEventsTeaser 
        title={t('home.upcomingTitle')}
        maxItems={4}
        context="homepage"
      />

      {/* Trending Events Section */}
      <section className="py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <TrendingEventsSection 
            limit={4} 
            title={t('home.trendingTitle')}
          />
        </div>
      </section>

      {/* Event Boost Ambassador Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 rounded-3xl p-8 md:p-12 border border-amber-200">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium mb-4">
                  <span>✨</span> {t('home.ambassadorBadge')}
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  {t('home.ambassadorTitle')}
                </h2>
                <p className="text-lg text-gray-600 mb-6">
                  {t('home.ambassadorDesc')}
                </p>
                <div className="flex flex-wrap gap-4">
                  <LocalizedLink to="/events">
                    <Button className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white">
                      {t('home.ambassadorStart')}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </LocalizedLink>
                  <LocalizedLink to="/boost/dashboard">
                    <Button variant="outline" className="border-orange-300 text-orange-600 hover:bg-orange-50">
                      {t('home.ambassadorDashboard')}
                    </Button>
                  </LocalizedLink>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-amber-100">
                  <div className="text-3xl font-bold text-orange-600 mb-1">🏆</div>
                  <p className="text-sm text-gray-600">{t('home.ambassadorBadges')}</p>
                </div>
                <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-amber-100">
                  <div className="text-3xl font-bold text-orange-600 mb-1">📊</div>
                  <p className="text-sm text-gray-600">{t('home.ambassadorStats')}</p>
                </div>
                <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-amber-100">
                  <div className="text-3xl font-bold text-orange-600 mb-1">🎁</div>
                  <p className="text-sm text-gray-600">{t('home.ambassadorRewards')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-orange-600 to-orange-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-600 to-orange-700" aria-hidden="true"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl font-bold mb-4">{t('home.ctaTitle')}</h2>
          <p className="text-xl mb-8 text-orange-100">
            {t('home.ctaDesc')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <LocalizedLink to="/events">
              <Button className="bg-white text-orange-600 hover:bg-gray-100 px-8 py-4 text-lg rounded-lg font-medium">
                {t('home.ctaBrowse')}
              </Button>
            </LocalizedLink>
            <a href="tel:08001214470">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-8 py-4 text-lg rounded-lg font-medium">
                {t('home.ctaCall')}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section - Visible for Perplexity and AI crawlers */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">
            {t('home.faqTitle')}
          </h2>
          <p className="text-center text-gray-600 mb-10">
            {t('home.faqSubtitle')}
          </p>
          
          <div className="space-y-6">
            {faqs.map((n) => (
              <article key={n} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {t(`home.faqQ${n}`)}
                </h3>
                <p
                  className="faq-answer text-gray-600"
                  dangerouslySetInnerHTML={{ __html: t(`home.faqA${n}`) }}
                />
              </article>
            ))}
          </div>
        </div>
      </section>
    </MainLayout>
  );
}
